import React, { useState, useEffect, useCallback, useRef, useContext, useMemo } from "react";
import Select from "react-select";
import { LoadingPortContext, resolvePageAccess } from "../../context/LoadingPortContext";

import { AgGridReact } from "@ag-grid-community/react";
import { ClientSideRowModelModule } from "@ag-grid-community/client-side-row-model";
import { RowGroupingModule } from "@ag-grid-enterprise/row-grouping";
import { MenuModule } from "@ag-grid-enterprise/menu";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import { Edit, Trash } from "lucide-react"
import redirectURL from "../redirectURL.js";
import SweetAlert from 'react-bootstrap-sweetalert';
import XLSX from "xlsx/dist/xlsx.full.min.js";
import { getCellValue, getUserIdentityFields } from "../common/utils";

// Consignee Holiday Calendar - last-mile consignee holidays.
// Same structure as hubOperatorMaster.js (AG Grid + Add/Edit slider + Import slider) plus a
// FullCalendar month view. "ALL" in warehouse and/or consignee is stored as ONE wildcard row
// (all warehouses / all consignees, including ones added later) instead of one row per consignee.
// process_gr_level_data_from_storage.py removes these holidays from hub waiting / last-mile /
// total transit time whenever a GR is processed.
const WEEKDAYS = [
    { value: 0, label: "Sun" }, { value: 1, label: "Mon" }, { value: 2, label: "Tue" },
    { value: 3, label: "Wed" }, { value: 4, label: "Thu" }, { value: 5, label: "Fri" },
    { value: 6, label: "Sat" }
];
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const ALL = "ALL";
const MAX_ROWS_PER_SAVE = 20000;

// "YYYY-MM-DD" <-> Date in UTC (holiday dates are plain calendar dates)
const ymdToDate = (ymd) => new Date(ymd + "T00:00:00.000Z");
const dateToYmd = (d) => d.toISOString().slice(0, 10);

const formatHolidayDate = (ymd) => {
    if (!ymd) return "";
    const p = ymd.split("-");
    return p[2] + "-" + MONTHS[Number(p[1]) - 1] + "-" + p[0];
};

// Every date between from and to (inclusive), optionally only on the given weekdays
const expandDates = (from, to, weekdays) => {
    if (!from) return [];
    const end = to || from;
    if (end < from) return [];
    const out = [];
    for (let d = ymdToDate(from); dateToYmd(d) <= end; d = new Date(d.getTime() + 86400000)) {
        if (!weekdays.length || weekdays.indexOf(d.getUTCDay()) !== -1) out.push(dateToYmd(d));
    }
    return out;
};

// Upload cell -> "YYYY-MM-DD": Excel date serial, YYYY-MM-DD, DD-MM-YYYY or DD/MM/YYYY
const parseUploadDate = (value) => {
    if (value === null || value === undefined || value === "") return null;
    let y, m, d;
    if (typeof value === "number") {
        const parsed = XLSX.SSF.parse_date_code(value);
        if (!parsed) return null;
        y = parsed.y; m = parsed.m; d = parsed.d;
    } else {
        const s = String(value).trim();
        let match = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
        if (match) { y = +match[1]; m = +match[2]; d = +match[3]; }
        else {
            match = s.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/);
            if (!match) return null;
            d = +match[1]; m = +match[2]; y = +match[3];
        }
    }
    const ymd = y + "-" + String(m).padStart(2, "0") + "-" + String(d).padStart(2, "0");
    const check = ymdToDate(ymd);
    return isNaN(check.getTime()) || dateToYmd(check) !== ymd ? null : ymd;
};

const isAll = (v) => String(v || "").trim().toUpperCase() === ALL;

const emptyForm = (port) => ({
    loading_port_code: port || "",
    consignee_codes: [],
    from_date: "",
    to_date: "",
    weekdays: [],
    holiday_name: ""
});

const ConsigneeHolidayCalendar = () => {

    const gridRef = useRef();
    const portContext = useContext(LoadingPortContext);
    const lastPortRef = useRef(portContext.loading_port_code);

    const modules = [
        ClientSideRowModelModule,
        RowGroupingModule,
        MenuModule
    ];

    var allowActionsRef = useRef(false);
    var allowDeleteRef = useRef(false);

    // ---------------- STATE ----------------
    const [rowData, setRowData] = useState([]);
    const [consigneeData, setConsigneeData] = useState([]); // consignees of the warehouse(s) in view
    const [loadingPortData, setLoadingPortData] = useState([]); // warehouses this user can access
    const [viewPort, setViewPort] = useState(""); // warehouse filter: a loading_port_code or ALL
    const [pageAccess, setPageAccess] = useState(0);
    const [selectedRows, setSelectedRows] = useState([]);

    const [viewMode, setViewMode] = useState("grid");
    const [filterConsignees, setFilterConsignees] = useState([]);
    const [filterYear, setFilterYear] = useState(String(new Date().getFullYear()));
    const [filterDate, setFilterDate] = useState(""); // set by clicking a calendar event

    const [showSlider, setShowSlider] = useState(false);
    const [formData, setFormData] = useState(emptyForm());
    const [editRow, setEditRow] = useState(null);

    const [showBulkSlider, setShowBulkSlider] = useState(false);
    const [errors, setErrors] = useState([]);
    const [validData, setValidData] = useState([]);
    const [fileName, setFileName] = useState("");
    const fileInputRef = useRef(null);

    const [loadshow, setLoadshow] = useState("show-n");
    const [overly, setOverly] = useState("show-n");
    const [show, setShow] = useState(false);
    const [basicType, setBasicType] = useState("");
    const [basicTitle, setBasicTitle] = useState("");

    const showAlert = (type, title) => {
        setShow(true);
        setBasicType(type);
        setBasicTitle(title);
    };

    // ---------------- WAREHOUSES / CONSIGNEES ----------------
    const portLabel = useCallback((code) => {
        if (isAll(code)) return "All warehouses";
        return (loadingPortData.find((each) => each.value == code) || {}).label || code || "";
    }, [loadingPortData]);

    const warehouseOptions = useMemo(
        () => [{ value: ALL, label: "All warehouses" }].concat(loadingPortData),
        [loadingPortData]
    );

    // consignee code (upper) -> [consignee records] (a code can exist under several warehouses)
    const consigneesByCode = useMemo(() => {
        const map = {};
        consigneeData.forEach((c) => {
            const key = String(c.consignee_code).trim().toUpperCase();
            (map[key] = map[key] || []).push(c);
        });
        return map;
    }, [consigneeData]);

    // One option per consignee code, optionally limited to one warehouse
    const consigneeOptionsFor = useCallback((port) => {
        const seen = {};
        return consigneeData
            .filter((c) => isAll(port) || !port || c.loading_port_code == port)
            .filter((c) => {
                const key = String(c.consignee_code).trim().toUpperCase();
                if (seen[key]) return false;
                seen[key] = true;
                return true;
            })
            .map((c) => ({
                value: c.consignee_code,
                label: c.consignee_code + " - " + (c.consignee_name || "") + (c.city ? " (" + c.city + ")" : "")
            }));
    }, [consigneeData]);

    // ---------------- DATA ----------------
    const portsInScope = (port) => (isAll(port) ? loadingPortData.map((p) => p.value) : [port]);

    const getConsignees = async (port, ports) => {
        try {
            const lists = await Promise.all(ports.map((p) =>
                redirectURL.post("/lmconsignees/getLmConsignees", { loading_port_code: p })
                    .then((response) => (Array.isArray(response.data) ? response.data : []))
                    .catch(() => [])
            ));
            const seen = {};
            setConsigneeData([].concat.apply([], lists).filter((c) => {
                const key = c.loading_port_code + "|" + String(c.consignee_code || "").trim();
                if (!String(c.consignee_code || "").trim() || seen[key]) return false;
                seen[key] = true;
                return true;
            }).sort((a, b) => String(a.consignee_name || "").localeCompare(String(b.consignee_name || ""))));
        } catch (error) {
            console.log(error);
            setConsigneeData([]);
        }
    };

    const getHolidays = async (ports) => {
        try {
            const response = await redirectURL.post("/masters/getConsigneeHolidays", { loading_port_codes: ports });
            if (response.data.status == "success") {
                setRowData((response.data.holidayData || []).map((each) => {
                    const ymd = String(each.holiday_date || "").slice(0, 10);
                    return {
                        ...each,
                        holiday_date: ymd,
                        holiday_day: ymd ? WEEKDAYS[ymdToDate(ymd).getUTCDay()].label : ""
                    };
                }));
            }
        } catch (error) {
            console.log(error);
        }
    };

    // ports: passed explicitly on first load, before loadingPortData state is set
    const getNecessaryData = async (port, ports) => {
        const scope = ports || portsInScope(port);
        await getConsignees(port, scope);
        await getHolidays(scope);
    };

    const reloadHolidays = () => getHolidays(portsInScope(viewPort));

    const saveHolidays = async (rows, successTitle) => {
        try {
            const response = await redirectURL.post("/masters/updateConsigneeHolidays", rows);
            if (response.data.status == "success") {
                showAlert("success", successTitle);
                return true;
            }
            showAlert("danger", response.data.message || "Save failed");
        } catch (error) {
            console.log(error);
            const msg = error.response && error.response.data && error.response.data.message;
            showAlert("danger", msg || "Save failed");
        }
        return false;
    };

    const deleteHolidays = async (rows) => {
        try {
            const response = await redirectURL.post("/masters/deleteConsigneeHolidays", rows);
            if (response.data.status == "success") {
                showAlert("success", "Deleted Succesfully");
            }
        } catch (error) {
            console.log(error);
        }
    };

    // ---------------- PAGE ACCESS / WAREHOUSE ----------------
    const pageAccessFn = useCallback(async () => {
        try {
            const resolved = await resolvePageAccess(window.location.pathname, portContext);
            if (!resolved) return;
            lastPortRef.current = resolved.loading_port_code;
            setPageAccess(resolved.pageAccess);
            setViewPort(resolved.loading_port_code);
            setLoadingPortData(resolved.lpNames);
            return resolved;
        } catch (err) {
            console.log(err);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [portContext]);

    useEffect(() => {
        const fetchData = async () => {
            setLoadshow("show-m");
            setOverly("show-m");
            const resolved = await pageAccessFn();
            if (resolved && resolved.loading_port_code) {
                await getNecessaryData(resolved.loading_port_code, [resolved.loading_port_code]);
            }
            setLoadshow("show-n");
            setOverly("show-n");
        };
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // The header's warehouse selector drives the default; the filter can switch to another or All.
    useEffect(() => {
        if (portContext.loading_port_code !== lastPortRef.current) {
            lastPortRef.current = portContext.loading_port_code;
            if (portContext.loading_port_code && loadingPortData.some((each) => each.value == portContext.loading_port_code)) {
                changeViewPort(portContext.loading_port_code);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [portContext.loading_port_code]);

    const changeViewPort = async (port) => {
        if (!port) return;
        setLoadshow("show-m");
        setOverly("show-m");
        setViewPort(port);
        setFilterConsignees([]);
        setFilterDate("");
        closeSlider();
        await getNecessaryData(port);
        setLoadshow("show-n");
        setOverly("show-n");
    };

    // ---------------- FILTERS ----------------
    const yearOptions = useMemo(() => {
        const years = new Set([String(new Date().getFullYear())]);
        rowData.forEach((r) => { if (r.holiday_date) years.add(r.holiday_date.slice(0, 4)); });
        return [{ value: "", label: "All years" }].concat(
            Array.from(years).sort().reverse().map((y) => ({ value: y, label: y }))
        );
    }, [rowData]);

    // ALL rows apply to every consignee, so they stay visible under a consignee filter
    const matchesConsigneeFilter = useCallback((r) => {
        return !filterConsignees.length || isAll(r.consignee_code) ||
            filterConsignees.some((o) => o.value === r.consignee_code);
    }, [filterConsignees]);

    const filteredRows = useMemo(() => rowData.filter((r) =>
        matchesConsigneeFilter(r) &&
        (!filterYear || String(r.holiday_date).slice(0, 4) === filterYear) &&
        (!filterDate || r.holiday_date === filterDate)
    ), [rowData, matchesConsigneeFilter, filterYear, filterDate]);

    // Calendar: one event per date + holiday name
    const calendarEvents = useMemo(() => {
        const groups = {};
        rowData.filter(matchesConsigneeFilter).forEach((r) => {
            const key = r.holiday_date + "|" + r.holiday_name;
            (groups[key] = groups[key] || []).push(r);
        });
        return Object.keys(groups).map((key) => {
            const list = groups[key];
            const first = list[0];
            const allRule = list.find((r) => isAll(r.consignee_code));
            let title;
            if (allRule) title = first.holiday_name + " - All consignees" + (isAll(allRule.loading_port_code) ? " (all warehouses)" : "");
            else if (list.length > 1) title = first.holiday_name + " (" + list.length + " consignees)";
            else title = first.holiday_name + " - " + (first.consignee_name || first.consignee_code);
            return {
                id: key,
                title: title,
                start: first.holiday_date,
                allDay: true,
                color: allRule ? "#2e7d32" : "#1976d2",
                extendedProps: { holiday_date: first.holiday_date }
            };
        });
    }, [rowData, matchesConsigneeFilter]);

    // ---------------- ADD / EDIT ----------------
    const formPort = formData.loading_port_code || viewPort;
    const formAllConsignees = formData.consignee_codes.some((o) => o.value === ALL);
    const formConsigneeCodes = formAllConsignees ? [ALL] : formData.consignee_codes.map((o) => o.value);
    const formDates = editRow ? [editRow.holiday_date] : expandDates(formData.from_date, formData.to_date, formData.weekdays);
    const formRowCount = editRow ? 1 : formConsigneeCodes.length * formDates.length;
    // Form warehouse: the filter's warehouse or All (consignees are loaded for that scope)
    const formWarehouseOptions = isAll(viewPort)
        ? warehouseOptions
        : warehouseOptions.filter((o) => isAll(o.value) || o.value == viewPort);

    const openAddSlider = (date) => {
        setEditRow(null);
        setFormData({ ...emptyForm(viewPort), from_date: date || "" });
        setShowSlider(true);
    };

    const handleEdit = (params) => {
        setEditRow(params.data);
        setFormData({ ...emptyForm(params.data.loading_port_code), holiday_name: params.data.holiday_name || "" });
        setShowSlider(true);
    };

    const closeSlider = () => {
        setShowSlider(false);
        setEditRow(null);
        setFormData(emptyForm());
        setShowBulkSlider(false);
        setValidData([]);
        setErrors([]);
        setFileName("");
    };

    // Saved consignee name/city for a code under a warehouse (ALL rules get a readable label)
    const consigneeInfo = (port, code) => {
        if (isAll(code)) return { consignee_name: "All consignees", city: "" };
        const list = consigneesByCode[String(code).trim().toUpperCase()] || [];
        return list.find((c) => c.loading_port_code == port) || list[0] || {};
    };

    const handleSave = async () => {
        const holidayName = String(formData.holiday_name || "").trim();
        if (!formPort) return showAlert("warning", "Select the Warehouse");
        if (!editRow && !formConsigneeCodes.length) return showAlert("warning", "Select at least one Consignee");
        if (!editRow && !formData.from_date) return showAlert("warning", "Select the Holiday Date");
        if (!editRow && formData.to_date && formData.to_date < formData.from_date) return showAlert("warning", "To Date must be on or after From Date");
        if (!editRow && !formDates.length) return showAlert("warning", "No dates match the selected weekdays in this range");
        if (!holidayName) return showAlert("warning", "Enter Holiday Name");
        if (formRowCount > MAX_ROWS_PER_SAVE) return showAlert("warning", formRowCount + " holidays selected - max " + MAX_ROWS_PER_SAVE + " per save. Narrow the range or consignees.");
        if (editRow && holidayName.toUpperCase() === String(editRow.holiday_name || "").trim().toUpperCase()) {
            showAlert("warning", "No Changes Made.");
            closeSlider();
            return;
        }

        const identity = getUserIdentityFields();
        const port = editRow ? editRow.loading_port_code : formPort;
        const codes = editRow ? [editRow.consignee_code] : formConsigneeCodes;
        const rows = [];
        codes.forEach((code) => {
            const c = consigneeInfo(port, code);
            formDates.forEach((date) => {
                rows.push({
                    loading_port_code: port,
                    consignee_code: code,
                    consignee_name: c.consignee_name || (editRow && editRow.consignee_name) || "",
                    consignee_city: c.city || (editRow && editRow.consignee_city) || "",
                    holiday_date: date,
                    holiday_name: holidayName,
                    ...identity
                });
            });
        });

        setLoadshow("show-m");
        setOverly("show-m");
        const ok = await saveHolidays(rows, editRow ? "Updated Succesfully" : rows.length + " holiday(s) saved");
        if (ok) {
            await reloadHolidays();
            closeSlider();
        }
        setLoadshow("show-n");
        setOverly("show-n");
    };

    // ---------------- DELETE ----------------
    const toDeletePayload = (r) => ({
        loading_port_code: r.loading_port_code,
        consignee_code: r.consignee_code,
        holiday_date: r.holiday_date,
        ...getUserIdentityFields()
    });

    const handleDelete = async (params) => {
        if (!window.confirm("Are you sure to delete this record?")) return;
        setLoadshow("show-m");
        setOverly("show-m");
        await deleteHolidays([toDeletePayload(params.data)]);
        await reloadHolidays();
        setLoadshow("show-n");
        setOverly("show-n");
    };

    const handleDeleteSelected = async () => {
        if (!window.confirm("Are you sure to delete " + selectedRows.length + " record(s)?")) return;
        setLoadshow("show-m");
        setOverly("show-m");
        await deleteHolidays(selectedRows.map(toDeletePayload));
        await reloadHolidays();
        setSelectedRows([]);
        setLoadshow("show-n");
        setOverly("show-n");
    };

    // ---------------- GRID ----------------
    const ActionRenderer = (props) => (
        <div className="d-flex justify-content-center align-items-center" style={{ gap: '6px', justifyContent: "center", width: "100%", height: "35px" }}>
            {allowActionsRef.current && <Edit className="text-primary cursor-pointer ml-2"
                size={15} style={{ cursor: "pointer" }} onClick={() => { handleEdit(props) }} />}
            {allowDeleteRef.current && <Trash className="text-danger cursor-pointer ml-2"
                size={15} style={{ cursor: "pointer" }} onClick={() => { handleDelete(props) }} />}
        </div>
    );
    const components = {
        actionRenderer: ActionRenderer
    };
    const columnDefs = [
        {
            headerName: "",
            width: 100,
            cellRenderer: "actionRenderer",
            pinned: "left",
            headerCheckboxSelection: allowDeleteRef.current ? true : false,
            headerCheckboxSelectionFilteredOnly: true,
            checkboxSelection: allowDeleteRef.current ? true : false,
            hide: (!allowActionsRef.current && !allowDeleteRef.current)
        },
        {
            headerName: "Warehouse", colId: "warehouse", width: 160,
            valueGetter: (p) => p.data ? portLabel(p.data.loading_port_code) : ""
        },
        {
            headerName: "Consignee Code", field: "consignee_code", width: 150,
            valueFormatter: (p) => isAll(p.value) ? "ALL" : p.value
        },
        { headerName: "Consignee Name", field: "consignee_name", width: 240 },
        { headerName: "City", field: "consignee_city", width: 140 },
        {
            headerName: "Holiday Date", field: "holiday_date", width: 140, sort: "asc",
            valueFormatter: (p) => formatHolidayDate(p.value)
        },
        { headerName: "Day", field: "holiday_day", width: 90 },
        { headerName: "Holiday Name", field: "holiday_name", width: 220 },
        { headerName: "Updated By", field: "updated_by", width: 200 },
        {
            headerName: "Updated At", field: "updated_at", width: 170,
            valueFormatter: (p) => p.value ? new Date(p.value).toLocaleString("en-IN", {
                day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit"
            }) : ""
        }
    ];

    const onExportClick = () => {
        if (gridRef.current && gridRef.current.api) {
            gridRef.current.api.exportDataAsExcel({
                fileName: "consignee_holiday_calendar.xlsx",
                columnKeys: ["warehouse", "consignee_code", "consignee_name", "consignee_city", "holiday_date", "holiday_day", "holiday_name", "updated_by", "updated_at"]
            });
        }
    };

    // ---------------- BULK UPLOAD ----------------
    const handleFileChange = (e) => {
        const file = e.target.files[0];
        e.target.value = ""; // reset immediately
        if (!file) return;
        setFileName(file.name);

        const reader = new FileReader();
        reader.readAsArrayBuffer(file);
        reader.onload = (event) => {
            const workbook = XLSX.read(new Uint8Array(event.target.result), { type: "array" });
            const sheet = workbook.Sheets[workbook.SheetNames[0]];
            const data = XLSX.utils.sheet_to_json(sheet, { defval: "" });
            validateExcelData(data);
        };
    };

    // warehouse cell -> loading_port_code or ALL (matches code or name, any case); null if unknown
    const resolveUploadPort = (value) => {
        const s = String(value || "").trim();
        if (isAll(s)) return ALL;
        const hit = loadingPortData.find((p) =>
            String(p.value).trim().toUpperCase() === s.toUpperCase() || String(p.label).trim().toUpperCase() === s.toUpperCase());
        return hit ? hit.value : null;
    };

    const validateExcelData = (data) => {
        const errorList = [];
        const cleanedData = [];
        const seenInFile = {};

        data.forEach((row, index) => {
            const rowNumber = index + 2; // Excel row (header = 1)
            const warehouseInput = String(getCellValue(row, "warehouse") || "").trim();
            const codeInput = String(getCellValue(row, "consignee_code") || "").trim();
            const dateInput = getCellValue(row, "holiday_date");
            const holidayName = String(getCellValue(row, "holiday_name") || "").trim();
            const rowErrors = [];

            if (!warehouseInput && !codeInput && !String(dateInput || "").trim() && !holidayName) return; // blank row

            // Warehouse: blank -> ALL for consignee ALL, else the filter's warehouse
            let port = warehouseInput ? resolveUploadPort(warehouseInput) : (isAll(codeInput) ? ALL : viewPort);
            if (warehouseInput && !port) {
                rowErrors.push(`Warehouse "${warehouseInput}" is not a warehouse you have access to (use ALL or a warehouse code).`);
            } else if (port && !isAll(port) && !isAll(viewPort) && port != viewPort) {
                rowErrors.push(`Warehouse "${warehouseInput}" is outside the selected filter - select "All warehouses" to upload for other warehouses.`);
            }

            let consigneeCode = codeInput;
            let consignee = null;
            if (!codeInput) {
                rowErrors.push("consignee_code is empty (use ALL for all consignees)");
            } else if (isAll(codeInput)) {
                consigneeCode = ALL;
            } else if (port) {
                const list = consigneesByCode[codeInput.toUpperCase()] || [];
                consignee = isAll(port) ? list[0] : list.find((c) => c.loading_port_code == port);
                if (!consignee) {
                    rowErrors.push(`Consignee "${codeInput}" does not exist in the consignee master for ${portLabel(port).toLowerCase() === "all warehouses" ? "any warehouse" : "warehouse " + portLabel(port)}.`);
                } else {
                    consigneeCode = consignee.consignee_code;
                }
            }

            const holidayDate = parseUploadDate(dateInput);
            if (!String(dateInput || "").trim()) {
                rowErrors.push("holiday_date is empty");
            } else if (!holidayDate) {
                rowErrors.push(`holiday_date "${dateInput}" is not a valid date (use YYYY-MM-DD or DD-MM-YYYY).`);
            }
            if (!holidayName) {
                rowErrors.push("holiday_name is empty");
            }

            if (!rowErrors.length) {
                const key = port + "|" + consigneeCode + "|" + holidayDate;
                if (seenInFile[key]) {
                    rowErrors.push(`Duplicate of row ${seenInFile[key]} (same warehouse, consignee and date).`);
                } else {
                    seenInFile[key] = rowNumber;
                }
            }

            if (rowErrors.length > 0) {
                errorList.push({ row: rowNumber, errors: rowErrors });
            } else {
                const info = consigneeInfo(port, consigneeCode);
                cleanedData.push({
                    loading_port_code: port,
                    consignee_code: consigneeCode,
                    consignee_name: info.consignee_name || "",
                    consignee_city: info.city || "",
                    holiday_date: holidayDate,
                    holiday_name: holidayName
                });
            }
        });

        if (cleanedData.length == 0 && errorList.length == 0) {
            errorList.push({ row: "", errors: ["No Data found. all fields are empty"] });
        }
        if (cleanedData.length > MAX_ROWS_PER_SAVE) {
            errorList.push({ row: "", errors: [cleanedData.length + " rows - max " + MAX_ROWS_PER_SAVE + " per upload"] });
            cleanedData.length = 0;
        }

        setErrors(errorList);
        setValidData(cleanedData);
    };

    const handleBulkUpload = async () => {
        setLoadshow("show-m");
        setOverly("show-m");
        const identity = getUserIdentityFields();
        const ok = await saveHolidays(
            validData.map((each) => ({ ...each, ...identity })),
            validData.length + " holiday(s) uploaded"
        );
        if (ok) {
            await reloadHolidays();
            closeSlider();
            if (fileInputRef.current) fileInputRef.current.value = "";
        }
        setLoadshow("show-n");
        setOverly("show-n");
    };

    var errTrueUpload = errors.length > 0 && validData.length > 0;
    var disableUpload = validData.length == 0;
    const getShortFileName = (name, maxLength = 31) => {
        const lastDot = name.lastIndexOf(".");
        const baseName = name.substring(0, lastDot);
        const extension = name.substring(lastDot);
        if (baseName.length <= maxLength) return name;
        return baseName.substring(0, maxLength) + "..." + extension;
    };

    const customStyles = {
        container: (provided) => ({ ...provided, width: "100%" })
    };
    const filterLabelStyle = { fontSize: "12px", fontWeight: 600, color: "#546e7a" };

    if (pageAccess != 2) {
        allowActionsRef.current = true;
    }
    if (pageAccess == 3) {
        allowDeleteRef.current = true;
    }
    return (
        <div className="hub-master-container d-flex flex-column" style={{ height: "100%", overflowY: "auto" }}>
            <SweetAlert
                show={show}
                type={basicType}
                title={basicTitle}
                onConfirm={() => setShow(false)}
                styles={{ zIndex: "10000001" }}
            >
            </SweetAlert>

            {/* HEADER */}
            <div className="master-header">
                <div className="d-flex align-items-center" style={{ gap: "14px" }}>
                    <div style={{ width: "5px", height: "26px", background: "linear-gradient(180deg,#1976d2,#42a5f5)", borderRadius: "3px", flexShrink: 0 }}></div>
                    <h5 style={{ margin: 0, fontSize: "16px", fontWeight: 700, color: "#1a237e", letterSpacing: "0.2px" }}>Consignee Holiday Calendar</h5>
                </div>

                <div className="d-flex align-items-center" style={{ gap: "8px" }}>
                    {(viewMode === "grid" && selectedRows.length > 0 && allowDeleteRef.current) && <button
                        className="pill-btn pill-btn-danger"
                        onClick={() => { handleDeleteSelected() }}
                    >
                        Delete Selected
                    </button>}
                    {viewMode === "grid" && <button
                        className="pill-btn pill-btn-primary"
                        onClick={() => { onExportClick() }}
                    >
                        Export Data
                    </button>}
                    {allowActionsRef.current && <button
                        className="pill-btn pill-btn-success"
                        onClick={() => openAddSlider("")}
                    >
                        + Add Holiday
                    </button>}
                    {allowActionsRef.current && <button
                        className="pill-btn pill-btn-info"
                        onClick={() => {
                            setErrors([]);
                            setValidData([]);
                            setFileName("");
                            setShowBulkSlider(true);
                        }}
                    >
                        Import Data
                    </button>}
                </div>
            </div>

            {/* FILTERS + VIEW TOGGLE */}
            <div className="d-flex align-items-end flex-wrap mt-2" style={{ gap: "16px" }}>
                <div style={{ width: "220px" }}>
                    <label className="m-0" style={filterLabelStyle}>Warehouse</label>
                    <Select
                        options={warehouseOptions}
                        value={warehouseOptions.find((each) => each.value == viewPort) || null}
                        onChange={(selected) => { if (selected && selected.value !== viewPort) changeViewPort(selected.value); }}
                        classNamePrefix="react-select"
                        isSearchable
                        styles={customStyles}
                    />
                </div>
                <div style={{ width: "360px" }}>
                    <label className="m-0" style={filterLabelStyle}>Consignee</label>
                    <Select
                        options={consigneeOptionsFor(viewPort)}
                        value={filterConsignees}
                        onChange={(selected) => { setFilterConsignees(selected || []); setFilterDate(""); }}
                        classNamePrefix="react-select"
                        placeholder="All consignees"
                        isMulti
                        isSearchable
                        closeMenuOnSelect={false}
                        styles={customStyles}
                    />
                </div>
                {viewMode === "grid" && <div style={{ width: "140px" }}>
                    <label className="m-0" style={filterLabelStyle}>Year</label>
                    <Select
                        options={yearOptions}
                        value={yearOptions.find((o) => o.value === filterYear) || yearOptions[0]}
                        onChange={(selected) => { setFilterYear(selected ? selected.value : ""); setFilterDate(""); }}
                        classNamePrefix="react-select"
                        styles={customStyles}
                    />
                </div>}
                {filterDate && viewMode === "grid" && (
                    <button className="pill-btn pill-btn-soft pill-btn-info" onClick={() => setFilterDate("")}>
                        {formatHolidayDate(filterDate)} ✕
                    </button>
                )}

                <div className="lttabs ml-auto">
                    <button type="button" className={viewMode === "grid" ? "activet" : ""} onClick={() => setViewMode("grid")}>
                        Grid View
                    </button>
                    <button type="button" className={viewMode === "calendar" ? "activet" : ""} onClick={() => setViewMode("calendar")}>
                        Calendar View
                    </button>
                </div>
            </div>

            {/* GRID */}
            {viewMode === "grid" && (
                <div className="ag-theme-balham grid-wrapper mt-3" style={{ flex: 1, minHeight: "420px" }}>
                    <AgGridReact
                        ref={gridRef}
                        modules={modules}
                        rowData={filteredRows}
                        columnDefs={columnDefs}
                        defaultColDef={{
                            sortable: true,
                            filter: true,
                            floatingFilter: true, resizable: true
                        }}
                        statusBar={{
                            statusPanels: [
                                { statusPanel: "agSelectedRowCountComponent", align: "left" },
                                { statusPanel: "agFilteredRowCountComponent", align: "center" },
                                { statusPanel: "agTotalRowCountComponent", align: "right" }
                            ]
                        }}
                        sideBar={{
                            toolPanels: [
                                { id: 'columns', labelDefault: 'Columns', iconKey: 'columns', toolPanel: 'agColumnsToolPanel' },
                                { id: 'filters', labelDefault: 'Filters', iconKey: 'filter', toolPanel: 'agFiltersToolPanel' }
                            ],
                        }}
                        animateRows rowSelection={"multiple"}
                        rowHeight={45}
                        headerHeight={45}
                        frameworkComponents={components}
                        onSelectionChanged={() => {
                            if (gridRef && gridRef.current && gridRef.current.api) {
                                setSelectedRows(gridRef.current.api.getSelectedRows());
                            }
                        }} suppressRowClickSelection={true}
                        overlayNoRowsTemplate="No holidays for the selected filters"
                    />
                </div>
            )}

            {/* CALENDAR */}
            {viewMode === "calendar" && (
                <div className="grid-wrapper mt-3 p-3" style={{ background: "#fff" }}>
                    <FullCalendar
                        plugins={[dayGridPlugin, interactionPlugin]}
                        initialView="dayGridMonth"
                        headerToolbar={{ left: "title", center: "", right: "today prev,next" }}
                        height="auto"
                        events={calendarEvents}
                        dayMaxEvents={3}
                        dateClick={(info) => { if (allowActionsRef.current) openAddSlider(info.dateStr); }}
                        eventClick={(info) => {
                            const date = info.event.extendedProps.holiday_date;
                            setFilterYear(date.slice(0, 4));
                            setFilterDate(date);
                            setViewMode("grid");
                        }}
                    />
                    <div className="mt-2" style={{ fontSize: "12px", color: "#667085" }}>
                        Green = applies to all consignees. Click a holiday to see it in the grid{allowActionsRef.current ? "; click a day to add a holiday." : "."}
                    </div>
                </div>
            )}

            {/* OVERLAY */}
            {(showSlider || showBulkSlider) && (
                <div className="slider-overlay" onClick={closeSlider} />
            )}

            {/* ADD / EDIT SLIDER */}
            <div className={`hub-slider ${showSlider ? "open" : ""} d-flex flex-column`}>
                <div className="slider-header">
                    {editRow ? "Edit Holiday" : "Add Holiday"}
                    <span onClick={closeSlider}>✕</span>
                </div>

                <div className="slider-body flex-grow-1 pt-4">
                    <label className="m-0">Warehouse<span className="text-danger">*</span></label>
                    <Select
                        options={formWarehouseOptions}
                        value={warehouseOptions.find((each) => each.value == (editRow ? editRow.loading_port_code : formPort)) || null}
                        onChange={(selected) => setFormData({ ...formData, loading_port_code: selected.value, consignee_codes: [] })}
                        classNamePrefix="react-select" className="mb-2"
                        isDisabled={!!editRow}
                        styles={customStyles}
                    />

                    {editRow ? (
                        <>
                            <label className="m-0">Consignee</label>
                            <input value={isAll(editRow.consignee_code) ? "ALL - All consignees" : editRow.consignee_code + " - " + (editRow.consignee_name || "")} disabled className="mb-2" />
                            <label className="m-0">Holiday Date</label>
                            <input value={formatHolidayDate(editRow.holiday_date) + " (" + editRow.holiday_day + ")"} disabled className="mb-2" />
                        </>
                    ) : (
                        <>
                            <label className="m-0">Consignee(s)<span className="text-danger">*</span></label>
                            <Select
                                options={[{ value: ALL, label: "ALL - All consignees" + (isAll(formPort) ? " of all warehouses" : " of " + portLabel(formPort)) }]
                                    .concat(consigneeOptionsFor(formPort))}
                                value={formData.consignee_codes}
                                onChange={(selected) => {
                                    const list = selected || [];
                                    const last = list[list.length - 1];
                                    setFormData({
                                        ...formData,
                                        consignee_codes: last && last.value === ALL ? [last] : list.filter((o) => o.value !== ALL)
                                    });
                                }}
                                classNamePrefix="react-select" className="mb-2"
                                isMulti
                                isSearchable
                                closeMenuOnSelect={false}
                                styles={customStyles}
                            />

                            <div className="d-flex" style={{ gap: "10px" }}>
                                <div style={{ flex: 1 }}>
                                    <label className="m-0">Holiday Date<span className="text-danger">*</span></label>
                                    <input type="date" value={formData.from_date} style={{ width: "100%" }}
                                        onChange={(e) => setFormData({ ...formData, from_date: e.target.value })} />
                                </div>
                                <div style={{ flex: 1 }}>
                                    <label className="m-0">To Date <small>(optional)</small></label>
                                    <input type="date" value={formData.to_date} min={formData.from_date || undefined} style={{ width: "100%" }}
                                        onChange={(e) => setFormData({ ...formData, to_date: e.target.value })} />
                                </div>
                            </div>

                            {formData.to_date && (
                                <div className="mt-2">
                                    <label className="m-0">Only on <small>(optional - e.g. every Sunday)</small></label>
                                    <div className="d-flex flex-wrap" style={{ gap: "6px" }}>
                                        {WEEKDAYS.map((w) => {
                                            const on = formData.weekdays.indexOf(w.value) !== -1;
                                            return (
                                                <button
                                                    key={w.value}
                                                    type="button"
                                                    className={"pill-btn " + (on ? "pill-btn-primary" : "pill-btn-soft pill-btn-info")}
                                                    style={{ padding: "4px 12px" }}
                                                    onClick={() => setFormData({
                                                        ...formData,
                                                        weekdays: on ? formData.weekdays.filter((d) => d !== w.value) : formData.weekdays.concat([w.value])
                                                    })}
                                                >
                                                    {w.label}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                        </>
                    )}

                    <label className="m-0 mt-2">Holiday Name<span className="text-danger">*</span></label>
                    <input
                        name="holiday_name"
                        value={formData.holiday_name}
                        placeholder="e.g. Diwali, Weekly Off"
                        onChange={(e) => setFormData({ ...formData, holiday_name: e.target.value })}
                    />

                    {!editRow && formRowCount > 0 && (
                        <div className="mt-3" style={{ fontSize: "13px", color: formRowCount > MAX_ROWS_PER_SAVE ? "#c62828" : "#1b5e20" }}>
                            {formAllConsignees
                                ? <>One rule per date for <strong>all consignees{isAll(formPort) ? " of all warehouses" : " of " + portLabel(formPort)}</strong> (includes consignees added later): <strong>{formDates.length}</strong> date(s).</>
                                : <>{formDates.length} date(s) × {formConsigneeCodes.length} consignee(s) = <strong>{formRowCount.toLocaleString("en-IN")}</strong> holiday(s).</>}
                            {" "}Existing ones on the same date are updated with this name.
                        </div>
                    )}
                    <div className="mt-2" style={{ fontSize: "12px", color: "#667085" }}>
                        Holidays are excluded from hub waiting, last-mile and total transit time when GRs are processed.
                    </div>
                </div>
                <div className="slider-footer">
                    <button
                        className="save-btn" style={{ margin: "0px", width: "auto", padding: "5px 25px" }}
                        onClick={handleSave}
                    >
                        {editRow ? "Update" : "Save"}
                    </button>
                </div>
            </div>

            {/* IMPORT SLIDER */}
            <div className={`hub-slider ${showBulkSlider ? "open" : ""} d-flex flex-column`}>
                <div className="slider-header">
                    Import Data
                    <span onClick={closeSlider}>✕</span>
                </div>

                <div className="slider-body flex-grow-1">
                    <div className="m-0" style={{ fontSize: "13px", color: "#546e7a" }}>
                        Columns: <strong>warehouse</strong> (optional), <strong>consignee_code</strong>, <strong>holiday_date</strong> (YYYY-MM-DD or DD-MM-YYYY), <strong>holiday_name</strong>.
                        <ul className="mb-0 pl-3">
                            <li>consignee_code <strong>ALL</strong> with blank warehouse: all warehouses and all consignees.</li>
                            <li>consignee_code <strong>ALL</strong> with a warehouse: all consignees of that warehouse.</li>
                            <li>A consignee code with blank warehouse: uses the Warehouse filter ({portLabel(viewPort)}).</li>
                        </ul>
                    </div>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept=".xlsx,.xls,.csv"
                        onChange={handleFileChange}
                        style={{ display: "none" }}
                    />
                    <div className="d-flex align-items-center gap-3">
                        <button
                            className="btn btn-info d-flex justify-content-center p-3"
                            type="button"
                            onClick={() => fileInputRef.current.click()}
                        >
                            Upload File
                        </button>
                        <a href={require("../../assets/consignee_holiday_template.csv")} download="consignee_holiday_template.csv"
                            className="btn btn-secondary d-flex justify-content-center ml-3 p-3 flex-grow-1">
                            Download Sample Template
                        </a>
                    </div>

                    {fileName && (
                        <div style={{ marginTop: "15px", display: "flex", gap: "6px", maxWidth: "410px" }}>
                            <span>Selected File :</span>
                            <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", flex: 1, fontWeight: "bold" }} title={fileName}>
                                {getShortFileName(fileName)}
                            </span>
                        </div>
                    )}
                    {fileName && validData.length > 0 && (
                        <div className="text-success">{validData.length.toLocaleString("en-IN")} valid row(s)</div>
                    )}

                    {errors.length > 0 && (
                        <>
                            <h6 className="text-danger mt-3">Validation Errors</h6>
                            <div style={{ flexGrow: 1, overflowY: "auto", width: "100%" }}>
                                <table className="table-bordered mb-0 err-table" style={{ width: "100%" }}>
                                    <thead className="table-light text-center" style={{ zIndex: "100", backgroundColor: "#efefef" }}>
                                        <tr>
                                            <th style={{ width: "60px", padding: "9px 15px" }}>Row</th>
                                            <th>Error Description</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {errors.map((err, i) => (
                                            <tr key={i}>
                                                <td className="text-center" style={{ padding: "9px 15px" }}>{err.row}</td>
                                                <td style={{ padding: "9px 15px", wordBreak: "break-word" }}>
                                                    {err.errors.map((e, idx) => (
                                                        <p className="m-0" key={idx}>{e}</p>
                                                    ))}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </>
                    )}
                </div>
                <div className="slider-footer">
                    <button
                        className="save-btn" style={{ margin: "0px", width: "auto", padding: "5px 25px", cursor: disableUpload && "not-allowed", opacity: disableUpload && "0.5" }}
                        onClick={handleBulkUpload}
                        disabled={disableUpload}
                    >
                        {errTrueUpload ? "Submit only valid data" : "Submit"}
                    </button>
                </div>
            </div>

            <div className={"dataLoadpageimg " + (loadshow)} style={{ zIndex: 100 }}>
                <div className="loader-box"><div className="loader-box"><div className="rotate dashed colored"></div></div></div>
            </div>
            <div className={"overlay-part " + (overly)}></div>
        </div>
    );
};

export default ConsigneeHolidayCalendar;
